// Cookie Consent Banner - GDPR Compliant
import { useState, useEffect } from "react";

import { Cookie, X, Settings, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts";

const COOKIE_CONSENT_KEY = "giftwish_cookie_consent";

const cookieTranslations = {
  ru: {
    title: "🍪 Мы используем cookies",
    description: "Мы используем файлы cookie для улучшения работы сайта, анализа трафика и персонализации контента.",
    acceptAll: "Принять все",
    acceptNecessary: "Только необходимые",
    settings: "Настройки",
    settingsTitle: "Настройки cookies",
    settingsDescription: "Выберите, какие категории cookies вы разрешаете использовать.",
    necessary: "Необходимые",
    necessaryDesc: "Обязательные для работы сайта. Не могут быть отключены.",
    analytics: "Аналитика",
    analyticsDesc: "Помогают понять, как посетители взаимодействуют с сайтом.",
    marketing: "Маркетинг",
    marketingDesc: "Используются для показа релевантной рекламы.",
    functional: "Функциональные",
    functionalDesc: "Запоминают ваши предпочтения и настройки.",
    save: "Сохранить настройки",
    learnMore: "Подробнее",
    privacyPolicy: "Политика конфиденциальности"
  },
  en: {
    title: "🍪 We use cookies",
    description: "We use cookies to improve site performance, analyze traffic, and personalize content.",
    acceptAll: "Accept all",
    acceptNecessary: "Necessary only",
    settings: "Settings",
    settingsTitle: "Cookie settings",
    settingsDescription: "Choose which cookie categories you allow.",
    necessary: "Necessary",
    necessaryDesc: "Required for the site to work. Cannot be disabled.",
    analytics: "Analytics",
    analyticsDesc: "Help understand how visitors interact with the site.",
    marketing: "Marketing",
    marketingDesc: "Used to show relevant ads.",
    functional: "Functional",
    functionalDesc: "Remember your preferences and settings.",
    save: "Save settings",
    learnMore: "Learn more",
    privacyPolicy: "Privacy Policy"
  },
  es: {
    title: "🍪 Usamos cookies",
    description: "Usamos cookies para mejorar el sitio, analizar el tráfico y personalizar el contenido.",
    acceptAll: "Aceptar todo",
    acceptNecessary: "Solo necesarias",
    settings: "Configuración",
    settingsTitle: "Configuración de cookies",
    settingsDescription: "Elija qué categorías de cookies permite.",
    necessary: "Necesarias",
    necessaryDesc: "Requeridas para que el sitio funcione.",
    analytics: "Analíticas",
    analyticsDesc: "Ayudan a entender cómo interactúan los visitantes.",
    marketing: "Marketing",
    marketingDesc: "Se usan para mostrar anuncios relevantes.",
    functional: "Funcionales",
    functionalDesc: "Recuerdan sus preferencias.",
    save: "Guardar",
    learnMore: "Más información",
    privacyPolicy: "Política de privacidad"
  },
  fr: {
    title: "🍪 Nous utilisons des cookies",
    description: "Nous utilisons des cookies pour améliorer le site, analyser le trafic et personnaliser le contenu.",
    acceptAll: "Tout accepter",
    acceptNecessary: "Nécessaires uniquement",
    settings: "Paramètres",
    settingsTitle: "Paramètres des cookies",
    settingsDescription: "Choisissez les catégories de cookies que vous autorisez.",
    necessary: "Nécessaires",
    necessaryDesc: "Requis pour le fonctionnement du site.",
    analytics: "Analytiques",
    analyticsDesc: "Aident à comprendre l'interaction des visiteurs.",
    marketing: "Marketing",
    marketingDesc: "Utilisés pour afficher des publicités pertinentes.",
    functional: "Fonctionnels",
    functionalDesc: "Mémorisent vos préférences.",
    save: "Enregistrer",
    learnMore: "En savoir plus",
    privacyPolicy: "Politique de confidentialité"
  },
  de: {
    title: "🍪 Wir verwenden Cookies",
    description: "Wir verwenden Cookies, um die Website zu verbessern, den Traffic zu analysieren und Inhalte zu personalisieren.",
    acceptAll: "Alle akzeptieren",
    acceptNecessary: "Nur notwendige",
    settings: "Einstellungen",
    settingsTitle: "Cookie-Einstellungen",
    settingsDescription: "Wählen Sie, welche Cookie-Kategorien Sie zulassen.",
    necessary: "Notwendig",
    necessaryDesc: "Erforderlich für die Funktion der Website.",
    analytics: "Analytik",
    analyticsDesc: "Helfen zu verstehen, wie Besucher interagieren.",
    marketing: "Marketing",
    marketingDesc: "Werden für relevante Werbung verwendet.",
    functional: "Funktional",
    functionalDesc: "Merken sich Ihre Einstellungen.",
    save: "Speichern",
    learnMore: "Mehr erfahren",
    privacyPolicy: "Datenschutzrichtlinie"
  },
  cs: {
    title: "🍪 Používáme cookies",
    description: "Používáme cookies pro zlepšení webu, analýzu návštěvnosti a personalizaci obsahu.",
    acceptAll: "Přijmout vše",
    acceptNecessary: "Pouze nezbytné",
    settings: "Nastavení",
    settingsTitle: "Nastavení cookies",
    settingsDescription: "Vyberte, které kategorie cookies povolujete.",
    necessary: "Nezbytné",
    necessaryDesc: "Vyžadovány pro fungování webu.",
    analytics: "Analytické",
    analyticsDesc: "Pomáhají pochopit interakci návštěvníků.",
    marketing: "Marketingové",
    marketingDesc: "Používány pro relevantní reklamu.",
    functional: "Funkční",
    functionalDesc: "Pamatují si vaše preference.",
    save: "Uložit",
    learnMore: "Více informací",
    privacyPolicy: "Zásady ochrany osobních údajů"
  },
  sk: {
    title: "🍪 Používame cookies",
    description: "Používame cookies na zlepšenie webu, analýzu návštevnosti a personalizáciu obsahu.",
    acceptAll: "Prijať všetko",
    acceptNecessary: "Iba nevyhnutné",
    settings: "Nastavenia",
    settingsTitle: "Nastavenia cookies",
    settingsDescription: "Vyberte, ktoré kategórie cookies povoľujete.",
    necessary: "Nevyhnutné",
    necessaryDesc: "Vyžadované pre fungovanie webu.",
    analytics: "Analytické",
    analyticsDesc: "Pomáhajú pochopiť interakciu návštevníkov.",
    marketing: "Marketingové",
    marketingDesc: "Používané pre relevantnú reklamu.",
    functional: "Funkčné",
    functionalDesc: "Pamätajú si vaše preferencie.",
    save: "Uložiť",
    learnMore: "Viac informácií",
    privacyPolicy: "Zásady ochrany osobných údajov"
  },
  uk: {
    title: "🍪 Ми використовуємо cookies",
    description: "Ми використовуємо файли cookie для покращення роботи сайту, аналізу трафіку та персоналізації контенту.",
    acceptAll: "Прийняти всі",
    acceptNecessary: "Лише необхідні",
    settings: "Налаштування",
    settingsTitle: "Налаштування cookies",
    settingsDescription: "Оберіть, які категорії cookies ви дозволяєте використовувати.",
    necessary: "Необхідні",
    necessaryDesc: "Обов'язкові для роботи сайту. Не можуть бути вимкнені.",
    analytics: "Аналітика",
    analyticsDesc: "Допомагають зрозуміти, як відвідувачі взаємодіють із сайтом.",
    marketing: "Маркетинг",
    marketingDesc: "Використовуються для показу релевантної реклами.",
    functional: "Функціональні",
    functionalDesc: "Запам'ятовують ваші налаштування та вподобання.",
    save: "Зберегти налаштування",
    learnMore: "Детальніше",
    privacyPolicy: "Політика конфіденційності"
  }
};

const CookieConsent = () => {
  const { language } = useLanguage();
  const t = cookieTranslations[language] || cookieTranslations.en;
  
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    // Initialize from localStorage synchronously
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) {
      try {
        const saved = JSON.parse(consent);
        return saved.preferences || {
          necessary: true,
          analytics: false,
          marketing: false,
          functional: false
        };
      } catch {
        // Invalid JSON, use defaults
      }
    }
    return {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    const consent = {
      timestamp: new Date().toISOString(),
      preferences: prefs
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(necessaryOnly);
    saveConsent(necessaryOnly);
  };

  const saveSettings = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === "necessary") return; // Can't disable necessary
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      
        {showBanner && !showSettings && (
          <div
            
            
            
            
            className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
            data-testid="cookie-banner"
          >
            <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
              <div className="p-4 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex w-12 h-12 bg-accent/10 rounded-xl items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-accent" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-semibold mb-1">{t.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t.description}{" "}
                      <a href="/privacy" className="text-accent hover:underline">{t.learnMore}</a>
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        onClick={acceptAll}
                        className="bg-accent text-accent-foreground hover:opacity-90"
                        data-testid="accept-all-cookies"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        {t.acceptAll}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={acceptNecessary}
                        data-testid="accept-necessary-cookies"
                      >
                        {t.acceptNecessary}
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={() => setShowSettings(true)}
                        data-testid="cookie-settings-btn"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        {t.settings}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5" />
              {t.settingsTitle}
            </DialogTitle>
            <DialogDescription>{t.settingsDescription}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Necessary Cookies */}
            <div className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{t.necessary}</span>
                <div className="w-11 h-6 bg-accent rounded-full relative cursor-not-allowed opacity-70">
                  <div className="absolute top-1 right-1 w-4 h-4 bg-white rounded-full" />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{t.necessaryDesc}</p>
            </div>

            {/* Analytics Cookies */}
            <div className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{t.analytics}</span>
                <button
                  onClick={() => togglePreference("analytics")}
                  className={`relative w-11 h-6 rounded-full transition-colors ${preferences.analytics ? 'bg-accent' : 'bg-muted'}`}
                  data-testid="toggle-analytics"
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${preferences.analytics ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <p className="text-sm text-muted-foreground">{t.analyticsDesc}</p>
            </div>

            {/* Marketing Cookies */}
            <div className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{t.marketing}</span>
                <button
                  onClick={() => togglePreference("marketing")}
                  className={`relative w-11 h-6 rounded-full transition-colors ${preferences.marketing ? 'bg-accent' : 'bg-muted'}`}
                  data-testid="toggle-marketing"
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${preferences.marketing ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <p className="text-sm text-muted-foreground">{t.marketingDesc}</p>
            </div>

            {/* Functional Cookies */}
            <div className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">{t.functional}</span>
                <button
                  onClick={() => togglePreference("functional")}
                  className={`relative w-11 h-6 rounded-full transition-colors ${preferences.functional ? 'bg-accent' : 'bg-muted'}`}
                  data-testid="toggle-functional"
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${preferences.functional ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <p className="text-sm text-muted-foreground">{t.functionalDesc}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={acceptNecessary}
              className="flex-1"
            >
              {t.acceptNecessary}
            </Button>
            <Button 
              onClick={saveSettings}
              className="flex-1 bg-accent text-accent-foreground hover:opacity-90"
              data-testid="save-cookie-settings"
            >
              {t.save}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
